import { Collapse, Input, Popover, Radio, Segmented, Select } from "antd";
import classNames from "classnames";
import cuid from "cuid";
import { isEmpty, isNil, uniq } from "lodash";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import ConditionsEditor from "shared/components/ConditionsEditor";
import { NAME_SPACES } from "shared/locales/constants";
import {
  DOCUMENT_TEMPLATE_AREAS,
  DOCUMENT_TEMPLATE_MOMENTS,
  GENERATED_DOCUMENTS_TYPES,
} from "utils/constants";
import {
  areaOfMoment,
  MOMENT_OPTIONS,
  momentKey,
  momentOfArea,
  normalizeArea,
  overlappingUseIndexes,
} from "utils/helpers/documentTemplates";
import { EMPTY_CONDITIONS, EMPTY_RULE } from "../../constants";
import { completeUses } from "./draft-content";

const GENERATION = { ALWAYS: "ALWAYS", ONLY_IF: "ONLY_IF" };
const LISTED_USES = 2;

export const NEW_USE = {
  insurancePlanId: null,
  type: null,
  area: DOCUMENT_TEMPLATE_AREAS.SUBSCRIPTION,
  outputName: null,
  conditions: null,
};

const FIRST_RULE_CONDITIONS = { ...EMPTY_CONDITIONS, rules: [EMPTY_RULE] };

const momentLabel = (area, t) => t(`MOMENTS.${momentKey(area)}`);

export const usageText = (use, planNames, t) =>
  t("USES.ITEM", {
    product: planNames[use.insurancePlanId] ?? use.insurancePlanId,
    moment: momentLabel(use.area, t),
  });

const usageSummary = (uses, planNames, t) => {
  if (uses.length <= LISTED_USES)
    return uses.map((use) => usageText(use, planNames, t)).join(" ; ");
  const areas = uniq(uses.map((use) => normalizeArea(use.area)));
  return areas.length === 1
    ? t("USES.MANY", { count: uses.length, moment: momentLabel(areas[0], t) })
    : t("USES.MANY_MOMENTS", { count: uses.length });
};

const popupContainer = (node) => node.parentNode;

const useRowKeys = (count) => {
  const keysRef = useRef([]);
  const keys = keysRef.current;
  while (keys.length < count) keys.push(cuid());
  keys.length = count;
  return { keys, forget: (index) => keys.splice(index, 1) };
};

const UseEditor = ({ use, plans, overlapping, onChange, onRemove }) => {
  const { t } = useTranslation(
    NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS_EDITOR,
  );
  const USES = t("USES", { returnObjects: true });
  const conditions = use.conditions;
  const setConditions = (next) => onChange({ ...use, conditions: next });

  return (
    <div
      className={classNames("dynamic-documents-editor__use", {
        "dynamic-documents-editor__use--overlap": overlapping,
      })}
    >
      <div className="dynamic-documents-editor__use-row">
        <Select
          showSearch
          size="small"
          optionFilterProp="label"
          className="dynamic-documents-editor__use-plan"
          placeholder={USES.PRODUCT_PLACEHOLDER}
          value={use.insurancePlanId ?? undefined}
          options={plans}
          getPopupContainer={popupContainer}
          onChange={(insurancePlanId) => onChange({ ...use, insurancePlanId })}
        />
        <button
          type="button"
          className="dynamic-documents-editor__link dynamic-documents-editor__link--danger"
          onClick={onRemove}
        >
          {USES.REMOVE}
        </button>
      </div>
      <Segmented
        size="small"
        value={momentOfArea(use.area)?.value ?? DOCUMENT_TEMPLATE_MOMENTS.BOTH}
        options={MOMENT_OPTIONS.map(({ value }) => ({
          value,
          label: t(`MOMENTS.${value}`),
        }))}
        onChange={(moment) => onChange({ ...use, area: areaOfMoment(moment) })}
      />
      {overlapping && (
        <p className="dynamic-documents-editor__warning">{USES.OVERLAP}</p>
      )}
      <Collapse
        ghost
        size="small"
        items={[
          {
            key: "advanced",
            label: USES.ADVANCED,
            children: (
              <div className="dynamic-documents-editor__use-advanced">
                <div className="dynamic-documents__config-field-label">
                  {USES.TYPE}
                </div>
                <Select
                  allowClear
                  size="small"
                  className="dynamic-documents__field-full"
                  placeholder={USES.TYPE_PLACEHOLDER}
                  value={use.type ?? undefined}
                  getPopupContainer={popupContainer}
                  options={GENERATED_DOCUMENTS_TYPES.map((value) => ({
                    value,
                    label: USES.TYPES[value] ?? value,
                  }))}
                  onChange={(type) => onChange({ ...use, type: type ?? null })}
                />
                <div className="dynamic-documents__config-field-label">
                  {USES.OUTPUT_NAME}
                </div>
                <Input
                  size="small"
                  placeholder={USES.OUTPUT_NAME_PLACEHOLDER}
                  value={use.outputName ?? ""}
                  onChange={(event) =>
                    onChange({ ...use, outputName: event.target.value || null })
                  }
                />
                <div className="dynamic-documents__config-field-label">
                  {USES.WHEN}
                </div>
                <Radio.Group
                  size="small"
                  value={
                    isNil(conditions) ? GENERATION.ALWAYS : GENERATION.ONLY_IF
                  }
                  onChange={(event) =>
                    setConditions(
                      event.target.value === GENERATION.ALWAYS
                        ? null
                        : FIRST_RULE_CONDITIONS,
                    )
                  }
                  options={[
                    { value: GENERATION.ALWAYS, label: USES.ALWAYS },
                    { value: GENERATION.ONLY_IF, label: USES.ONLY_IF },
                  ]}
                />
                {!isNil(conditions) && (
                  <ConditionsEditor
                    conditions={conditions}
                    emptyMessage={USES.CONDITIONS_EMPTY}
                    onOperatorChange={(operator) =>
                      setConditions({ ...conditions, operator })
                    }
                    onAddRule={() =>
                      setConditions({
                        ...conditions,
                        rules: [...conditions.rules, EMPTY_RULE],
                      })
                    }
                    onRemoveRule={(index) =>
                      setConditions({
                        ...conditions,
                        rules: conditions.rules.filter(
                          (_, position) => position !== index,
                        ),
                      })
                    }
                    onUpdateRule={(index, patch) =>
                      setConditions({
                        ...conditions,
                        rules: conditions.rules.map((rule, position) =>
                          position === index ? { ...rule, ...patch } : rule,
                        ),
                      })
                    }
                  />
                )}
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};

const UsesEditor = ({ uses, plans, onChange }) => {
  const { t } = useTranslation(
    NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS_EDITOR,
  );
  const USES = t("USES", { returnObjects: true });
  const overlapping = new Set(overlappingUseIndexes(uses));
  const { keys, forget } = useRowKeys(uses.length);

  return (
    <div className="dynamic-documents-editor__uses-editor">
      <h4 className="dynamic-documents-editor__step-title">{USES.TITLE}</h4>
      {isEmpty(uses) && (
        <p className="dynamic-documents-editor__muted">{USES.EMPTY}</p>
      )}
      {uses.map((use, index) => (
        <UseEditor
          key={keys[index]}
          use={use}
          plans={plans}
          overlapping={overlapping.has(index)}
          onChange={(next) =>
            onChange(
              uses.map((current, position) =>
                position === index ? next : current,
              ),
            )
          }
          onRemove={() => {
            forget(index);
            onChange(uses.filter((_, position) => position !== index));
          }}
        />
      ))}
      <button
        type="button"
        className="dynamic-documents-editor__link"
        onClick={() => onChange([...uses, NEW_USE])}
      >
        {USES.ADD}
      </button>
      <p className="dynamic-documents-editor__muted">{USES.NOTHING_LIVE}</p>
    </div>
  );
};

const UsageLine = ({
  uses,
  plans,
  planNames,
  open,
  onOpenChange,
  onChange,
}) => {
  const { t } = useTranslation(
    NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS_EDITOR,
  );
  const USES = t("USES", { returnObjects: true });
  const complete = completeUses(uses);
  const none = isEmpty(complete);

  const changeOpen = (next) => {
    if (next && isEmpty(uses)) onChange([NEW_USE]);
    if (!next && complete.length !== uses.length) onChange(complete);
    onOpenChange(next);
  };

  const addUse = () => {
    onChange([...uses, NEW_USE]);
    onOpenChange(true);
  };

  const many = complete.length > LISTED_USES;

  return (
    <div className="dynamic-documents-editor__usage">
      {none ? (
        <span className="dynamic-documents-editor__usage-none">
          {USES.USED_IN} {USES.NONE}
        </span>
      ) : (
        <>
          <span className="dynamic-documents-editor__usage-label">
            {USES.USED_IN}
          </span>
          <strong
            className="dynamic-documents-editor__usage-summary"
            title={complete
              .map((use) => usageText(use, planNames, t))
              .join("\n")}
          >
            {usageSummary(complete, planNames, t)}
          </strong>
        </>
      )}
      <Popover
        open={open}
        onOpenChange={changeOpen}
        trigger="click"
        placement="bottomLeft"
        overlayClassName="dynamic-documents-editor__uses-popover"
        content={<UsesEditor uses={uses} plans={plans} onChange={onChange} />}
      >
        <button type="button" className="dynamic-documents-editor__link">
          {none ? USES.CHOOSE : many ? USES.SEE_EDIT : USES.EDIT}
        </button>
      </Popover>
      {!none && (
        <button
          type="button"
          className="dynamic-documents-editor__link"
          onClick={addUse}
        >
          {USES.ADD}
        </button>
      )}
    </div>
  );
};

export default UsageLine;
